import { useMemo, useState } from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Area,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const METRIC_CONFIG = {
    mrr: { label: 'MRR', color: '#34d399', format: 'currency' },
    clients: { label: 'Clientes', color: '#22d3ee', format: 'number' },
    churnRate: { label: 'Churn %', color: '#f87171', format: 'percent' },
    arpu: { label: 'ARPU', color: '#a78bfa', format: 'currency' },
    cashFlow: { label: 'Caixa Projetado', color: '#fbbf24', format: 'currency' },
};

function formatValue(value, format) {
    if (value == null) return '—';
    switch (format) {
        case 'currency':
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0 }).format(value);
        case 'percent':
            return `${value.toFixed(1)}%`;
        case 'number':
            return value.toFixed(0);
        default:
            return value;
    }
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;

    return (
        <div className="bg-surface border border-border-strong rounded-lg p-3 shadow-xl">
            <p className="text-foreground-muted text-xs font-medium mb-2">{label}</p>
            {payload.map((entry, i) => {
                const config = Object.values(METRIC_CONFIG).find(c => c.color === entry.color) || {};
                const isProjection = entry.dataKey?.includes('Proj');
                return (
                    <div key={i} className="flex items-center gap-2 text-xs">
                        <div
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: entry.color, opacity: isProjection ? 0.6 : 1 }}
                        />
                        <span className="text-foreground-muted">
                            {entry.name}:
                        </span>
                        <span className="text-foreground-strong font-medium">
                            {formatValue(entry.value, config.format || 'currency')}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Weighted Linear Regression (WLR)
 * Fits y = a + b*x with exponential decay weights (recent observations weighted more)
 * Returns { intercept, slope, predict(x), r2 }
 */
function weightedLinearRegression(values, decayFactor = 0.85) {
    const n = values.length;
    if (n === 0) return { intercept: 0, slope: 0, predict: () => 0, r2: 0 };
    if (n === 1) return { intercept: values[0], slope: 0, predict: () => values[0], r2: 0 };

    const weights = values.map((_, i) => Math.pow(decayFactor, n - 1 - i));

    let sumW = 0, sumWX = 0, sumWY = 0, sumWXX = 0, sumWXY = 0;
    for (let i = 0; i < n; i++) {
        const w = weights[i];
        sumW += w;
        sumWX += w * i;
        sumWY += w * values[i];
        sumWXX += w * i * i;
        sumWXY += w * i * values[i];
    }

    const denom = sumW * sumWXX - sumWX * sumWX;
    if (Math.abs(denom) < 1e-10) {
        const avg = sumWY / sumW;
        return { intercept: avg, slope: 0, predict: () => avg, r2: 0 };
    }

    const slope = (sumW * sumWXY - sumWX * sumWY) / denom;
    const intercept = (sumWY - slope * sumWX) / sumW;

    // R² (coefficient of determination)
    const yMean = sumWY / sumW;
    let ssRes = 0, ssTot = 0;
    for (let i = 0; i < n; i++) {
        const predicted = intercept + slope * i;
        ssRes += weights[i] * (values[i] - predicted) ** 2;
        ssTot += weights[i] * (values[i] - yMean) ** 2;
    }
    const r2 = ssTot > 0 ? 1 - ssRes / ssTot : 0;

    return { intercept, slope, predict: (x) => intercept + slope * x, r2 };
}

/**
 * Exponential Weighted Moving Average (EWMA)
 * Higher alpha = more responsive to recent values
 */
function ewma(values, alpha = 0.3) {
    if (values.length === 0) return 0;
    let result = values[0];
    for (let i = 1; i < values.length; i++) {
        result = alpha * values[i] + (1 - alpha) * result;
    }
    return result;
}

/**
 * Generate trend data from companies with projections
 *
 * Modelo matemático:
 *   1. Aquisição de clientes: WLR (tendência linear ponderada) sobre newClients/mês
 *   2. Churn mensal: EWMA (suavização exponencial) sobre taxa histórica
 *   3. ARPU: EWMA com drift limitado (±3%/mês max)
 *   4. Clientes[m] = Clientes[m-1] + Novos[m] - Churned[m]  (stock-flow)
 *   5. MRR[m] = Clientes[m] × ARPU[m]
 *   6. Caixa[m] = Caixa[m-1] + MRR[m]  (receita acumulada)
 */
export function generateTrendData(companies, billingData) {
    if (!companies?.length) return [];

    const now = new Date();
    const monthsBack = 12;
    const monthsForward = 6;

    // Build historical monthly data
    const monthlyData = {};

    for (let i = monthsBack; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = d.toISOString().slice(0, 7);
        const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });
        monthlyData[key] = {
            month: label,
            monthKey: key,
            mrr: 0,
            clients: 0,
            newClients: 0,
            churned: 0,
            churnRate: 0,
            arpu: 0,
            isProjection: false,
        };
    }

    // Fill historical data
    for (const c of companies) {
        const createdMonth = c.createdAt?.slice(0, 7);
        const isChurned = c.status === 'churned' || c.status === 'suspended';
        const churnMonth = isChurned && c.updatedAt ? c.updatedAt.slice(0, 7) : null;

        const billing = billingData?.[c.cnpj];
        const subValue = billing?.subscriptions?.[0]?.value;
        const clientMrr = Number(subValue || c.plan?.monthlyPrice || 0);

        for (const [monthKey, data] of Object.entries(monthlyData)) {
            // Was this client active in this month?
            if (createdMonth && createdMonth <= monthKey) {
                if (!churnMonth || churnMonth > monthKey) {
                    data.clients++;
                    data.mrr += clientMrr;
                }
            }
            // New client this month?
            if (createdMonth === monthKey) {
                data.newClients++;
            }
            // Churned this month?
            if (churnMonth === monthKey) {
                data.churned++;
            }
        }
    }

    // Calculate derived metrics for historical
    const historicalEntries = Object.values(monthlyData).sort((a, b) => a.monthKey.localeCompare(b.monthKey));
    for (const data of historicalEntries) {
        data.arpu = data.clients > 0 ? data.mrr / data.clients : 0;
        const totalBase = data.clients + data.churned;
        data.churnRate = totalBase > 0 ? (data.churned / totalBase) * 100 : 0;
    }

    // ============================================================
    // PROJECTION MODEL — Stock-Flow com WLR + EWMA
    // ============================================================

    // Janela de análise: meses com alguma atividade, mín. 6 períodos
    const activeMonths = historicalEntries.filter(m => m.clients > 0 || m.newClients > 0);
    const analysisWindow = activeMonths.length >= 6
        ? activeMonths
        : historicalEntries.slice(Math.max(0, historicalEntries.length - 6));

    // --- 1. Aquisição de clientes: WLR sobre série de novos clientes/mês ---
    const newClientSeries = analysisWindow.map(m => m.newClients);
    const acquisitionModel = weightedLinearRegression(newClientSeries, 0.85);
    // Teto de aquisição: no máximo 2× o pico histórico ou +3 absolutos/mês
    const historicalMaxNew = Math.max(...newClientSeries, 1);
    const acquisitionCap = Math.max(historicalMaxNew * 2, 3);

    // --- 2. Churn mensal: EWMA (α=0.3) suavizado, cap 0–20% ---
    const churnSeries = analysisWindow.map(m => m.churnRate / 100);
    const projectedChurnDecimal = Math.max(0, Math.min(ewma(churnSeries, 0.3), 0.2));

    // --- 3. ARPU: EWMA (α=0.4) com drift limitado a ±3%/mês ---
    const arpuSeries = analysisWindow.filter(m => m.arpu > 0).map(m => m.arpu);
    const baseArpu = arpuSeries.length > 0 ? ewma(arpuSeries, 0.4) : 0;
    const arpuModel = weightedLinearRegression(arpuSeries, 0.85);
    const maxArpuDrift = baseArpu * 0.03;
    const cappedArpuSlope = Math.min(Math.max(arpuModel.slope, -maxArpuDrift), maxArpuDrift);

    // --- 4. Stock-Flow: projetar mês a mês ---
    const lastData = historicalEntries[historicalEntries.length - 1];
    let projClients = lastData?.clients || 0;
    let cumulativeCash = lastData?.mrr || 0; // ponto de partida = MRR atual
    const baseIdx = newClientSeries.length; // índice de continuação para WLR

    const projections = [];
    for (let i = 1; i <= monthsForward; i++) {
        const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
        const label = d.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

        // Novos clientes: previsão WLR, capped, mín 0
        const rawPrediction = acquisitionModel.predict(baseIdx + i - 1);
        const predictedNew = Math.max(0, Math.min(Math.round(rawPrediction), acquisitionCap));

        // Churned: taxa EWMA aplicada à base atual
        const predictedChurned = Math.round(projClients * projectedChurnDecimal);

        // Balanço líquido de clientes
        projClients = Math.max(0, projClients + predictedNew - predictedChurned);

        // ARPU com drift suave
        const projArpu = Math.max(0, baseArpu + cappedArpuSlope * i);

        // MRR = clientes × ARPU
        const projMrr = projClients * projArpu;

        // Receita acumulada
        cumulativeCash += projMrr;

        projections.push({
            month: label,
            monthKey: d.toISOString().slice(0, 7),
            mrrProj: Math.round(projMrr * 100) / 100,
            clientsProj: projClients,
            churnRateProj: Math.round(projectedChurnDecimal * 10000) / 100,
            arpuProj: Math.round(projArpu * 100) / 100,
            cashFlow: Math.round(cumulativeCash * 100) / 100,
            newClientsProj: predictedNew,
            churnedProj: predictedChurned,
            isProjection: true,
        });
    }

    // Merge: last historical point gets projection values too for seamless line
    if (historicalEntries.length > 0) {
        const last = historicalEntries[historicalEntries.length - 1];
        last.mrrProj = last.mrr;
        last.clientsProj = last.clients;
        last.churnRateProj = last.churnRate;
        last.arpuProj = last.arpu;
        last.cashFlow = last.mrr;
    }

    return [...historicalEntries, ...projections];
}

export default function TrendChart({ companies, billingData }) {
    const [visibleMetrics, setVisibleMetrics] = useState({
        mrr: true,
        clients: false,
        churnRate: false,
        cashFlow: true,
    });

    const data = useMemo(
        () => generateTrendData(companies, billingData),
        [companies, billingData]
    );

    const toggleMetric = (key) => {
        setVisibleMetrics(prev => ({ ...prev, [key]: !prev[key] }));
    };

    if (!data.length) {
        return (
            <div className="bg-surface/60 border border-border rounded-xl p-6 text-center text-foreground-muted">
                Dados insuficientes para gerar gráfico de tendência
            </div>
        );
    }

    // Find where projections start
    const projectionStartIndex = data.findIndex(d => d.isProjection);

    return (
        <div className="bg-surface/60 border border-border rounded-xl p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-cyan-700 dark:text-cyan-400" />
                    <h3 className="text-sm font-semibold text-foreground">
                        Tendência & Projeção
                    </h3>
                    <span className="text-[10px] text-foreground-muted bg-surface-active px-2 py-0.5 rounded-full">
                        12m histórico · 6m projeção
                    </span>
                </div>

                {/* Metric toggles */}
                <div className="flex items-center gap-1">
                    {Object.entries(METRIC_CONFIG).map(([key, config]) => (
                        <button
                            key={key}
                            onClick={() => toggleMetric(key)}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium transition-all ${visibleMetrics[key]
                                    ? 'bg-surface-active text-foreground'
                                    : 'text-foreground-muted hover:text-foreground-muted'
                                }`}
                        >
                            <div
                                className="w-1.5 h-1.5 rounded-full"
                                style={{
                                    backgroundColor: config.color,
                                    opacity: visibleMetrics[key] ? 1 : 0.3,
                                }}
                            />
                            {config.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                    <defs>
                        {Object.entries(METRIC_CONFIG).map(([key, config]) => (
                            <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={config.color} stopOpacity={0.15} />
                                <stop offset="100%" stopColor={config.color} stopOpacity={0} />
                            </linearGradient>
                        ))}
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />

                    <XAxis
                        dataKey="month"
                        tick={{ fill: '#737373', fontSize: 10 }}
                        axisLine={{ stroke: '#404040' }}
                        tickLine={false}
                    />

                    <YAxis
                        yAxisId="left"
                        tick={{ fill: '#737373', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                        tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                    />

                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fill: '#737373', fontSize: 10 }}
                        axisLine={false}
                        tickLine={false}
                    />

                    <Tooltip content={<CustomTooltip />} />

                    {/* Projection divider */}
                    {projectionStartIndex > 0 && (
                        <ReferenceLine
                            x={data[projectionStartIndex - 1]?.month}
                            stroke="#525252"
                            strokeDasharray="4 4"
                            label={{
                                value: 'Projeção →',
                                position: 'top',
                                fill: '#737373',
                                fontSize: 10,
                            }}
                            yAxisId="left"
                        />
                    )}

                    {/* Historical MRR */}
                    {visibleMetrics.mrr && (
                        <>
                            <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="mrr"
                                name="MRR"
                                stroke={METRIC_CONFIG.mrr.color}
                                fill={`url(#gradient-mrr)`}
                                strokeWidth={2}
                                dot={false}
                                connectNulls={false}
                            />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="mrrProj"
                                name="MRR (proj.)"
                                stroke={METRIC_CONFIG.mrr.color}
                                strokeWidth={2}
                                strokeDasharray="6 4"
                                dot={false}
                                connectNulls
                                opacity={0.6}
                            />
                        </>
                    )}

                    {/* Historical Clients */}
                    {visibleMetrics.clients && (
                        <>
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="clients"
                                name="Clientes"
                                stroke={METRIC_CONFIG.clients.color}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="clientsProj"
                                name="Clientes (proj.)"
                                stroke={METRIC_CONFIG.clients.color}
                                strokeWidth={2}
                                strokeDasharray="6 4"
                                dot={false}
                                connectNulls
                                opacity={0.6}
                            />
                        </>
                    )}

                    {/* Churn Rate */}
                    {visibleMetrics.churnRate && (
                        <>
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="churnRate"
                                name="Churn %"
                                stroke={METRIC_CONFIG.churnRate.color}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="churnRateProj"
                                name="Churn % (proj.)"
                                stroke={METRIC_CONFIG.churnRate.color}
                                strokeWidth={2}
                                strokeDasharray="6 4"
                                dot={false}
                                connectNulls
                                opacity={0.6}
                            />
                        </>
                    )}

                    {/* ARPU */}
                    {visibleMetrics.arpu && (
                        <>
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="arpu"
                                name="ARPU"
                                stroke={METRIC_CONFIG.arpu.color}
                                strokeWidth={2}
                                dot={false}
                            />
                            <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey="arpuProj"
                                name="ARPU (proj.)"
                                stroke={METRIC_CONFIG.arpu.color}
                                strokeWidth={2}
                                strokeDasharray="6 4"
                                dot={false}
                                connectNulls
                                opacity={0.6}
                            />
                        </>
                    )}

                    {/* Cash Flow Projection */}
                    {visibleMetrics.cashFlow && (
                        <Area
                            yAxisId="left"
                            type="monotone"
                            dataKey="cashFlow"
                            name="Caixa Projetado"
                            stroke={METRIC_CONFIG.cashFlow.color}
                            fill={`url(#gradient-cashFlow)`}
                            strokeWidth={2}
                            strokeDasharray="4 3"
                            dot={false}
                            connectNulls
                            opacity={0.7}
                        />
                    )}
                </ComposedChart>
            </ResponsiveContainer>

            {/* Summary row */}
            <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-border">
                {data.length > 0 && (() => {
                    const histData = data.filter(d => !d.isProjection);
                    const projData = data.filter(d => d.isProjection);
                    if (!projData.length || !histData.length) return null;

                    const endMrr = projData.at(-1).mrrProj || 0;
                    const startMrr = histData.at(-1).mrr || 0;
                    const mrrGrowth = startMrr > 0 ? ((endMrr - startMrr) / startMrr * 100) : 0;
                    const endClients = projData.at(-1).clientsProj || 0;
                    const totalCash = projData.at(-1).cashFlow || 0;
                    const avgNewMonth = Math.round(
                        projData.reduce((s, p) => s + (p.newClientsProj || 0), 0) / projData.length
                    );
                    const churnPct = projData[0]?.churnRateProj || 0;
                    const projArpu = projData[0]?.arpuProj || 0;
                    const ltv = churnPct > 0 ? projArpu / (churnPct / 100) : 0;

                    return (
                        <>
                            <div className="flex items-center gap-4 flex-wrap">
                                <div className="text-[11px]">
                                    <span className="text-foreground-muted">MRR em 6m: </span>
                                    <span className="text-emerald-700 dark:text-emerald-400 font-medium">
                                        {formatValue(endMrr, 'currency')}
                                    </span>
                                    <span className={`ml-1 ${mrrGrowth >= 0 ? 'text-emerald-500' : 'text-red-700 dark:text-red-400'}`}>
                                        ({mrrGrowth >= 0 ? '+' : ''}{mrrGrowth.toFixed(0)}%)
                                    </span>
                                </div>
                                <div className="text-[11px]">
                                    <span className="text-foreground-muted">Clientes em 6m: </span>
                                    <span className="text-cyan-700 dark:text-cyan-400 font-medium">{endClients}</span>
                                </div>
                                <div className="text-[11px]">
                                    <span className="text-foreground-muted">Receita acum.: </span>
                                    <span className="text-yellow-700 dark:text-yellow-400 font-medium">
                                        {formatValue(totalCash, 'currency')}
                                    </span>
                                </div>
                                {ltv > 0 && (
                                    <div className="text-[11px]">
                                        <span className="text-foreground-muted">LTV: </span>
                                        <span className="text-violet-700 dark:text-violet-400 font-medium">
                                            {formatValue(ltv, 'currency')}
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="text-[10px] text-foreground-muted">
                                Modelo: ~{avgNewMonth} novos/mês · {churnPct.toFixed(1)}% churn ·{' '}
                                {formatValue(projArpu, 'currency')} ARPU médio
                            </div>
                        </>
                    );
                })()}
            </div>
        </div>
    );
}
